import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { FilterParams, ProcessedEvent } from '../../core/models/habilitation-history.model';
import { AuthorizationHistoryService } from '../../core/services/habilitation.service';
import { MatIcon } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { CalendarDialogComponent } from '../../components/calendar-dialog.component';

@Component({
  selector: 'app-historiques',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    CommonModule,
    FormsModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule
  ],
  templateUrl: './historiques.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./historiques.component.scss']
})
export class HistoriquesComponent implements OnInit, OnDestroy {

  @ViewChild('calendarPicker') calendarPicker!: MatDatepicker<Date>;
  private destroy$ = new Subject<void>();

  events: ProcessedEvent[] = [];
  filteredEvents: ProcessedEvent[] = [];
  paginatedEvents: ProcessedEvent[] = [];
  error: string | null = null;

  adminsList: any[] = [];

  totalEvents = 0;
  pageSize = 10;
  currentPage = 0;

  todayEventsCount = 0;
  pagination: any = null;

  selectedPeriod = '';
  selectedDate: Date | null = null;

  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;

  currentFilters: FilterParams = {
    page: 0,
    size: 50,
    sort: 'time,desc',
    clientId: 'habilitation',
    clientUuid: '2ee236ea-871f-425a-bde0-33d412dec6a7',
    startDate: undefined
  };

  selectedOperationType = '';
  selectedAdmin = '';
  isLoading = true;

  // Propriétés pour la pagination personnalisée
  totalPages = 0;
  displayPages: (number | string)[] = [];

  constructor(private authHistoryService: AuthorizationHistoryService, private dialog: MatDialog) {}

  ngOnInit(): void {
    const cachedEvents = localStorage.getItem('processedEvents');
    if (cachedEvents) {
      this.events = JSON.parse(cachedEvents);
      this.extractAdminsList();
      this.applyLocalFilters();
      this.isLoading = false;
    } else {
      this.loadHistoriques();
      this.subscribeToServiceState();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.authHistoryService.resetState();
  }

  loadHistoriques(): void {
    this.isLoading = true;
    this.authHistoryService.getAllHistoriques(this.currentFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.events = (response.content as ProcessedEvent[]).map(event => {
            const eventDate = new Date(event.time);
            return {
              ...event,
              dateString: eventDate.toLocaleDateString(),
              timeString: eventDate.toLocaleTimeString(),
              operationClass: event.operationType.toLowerCase(),
              userId: event.authDetails?.user?.id || '',
              username: event.authDetails?.user?.username || '',
              userFullName: `${event.authDetails?.user?.firstName || ''} ${event.authDetails?.user?.lastName || ''}`,
              userEmail: event.authDetails?.user?.email || '',
              ipAddress: event.authDetails?.ipAddress || ''
            };
          });

          localStorage.setItem('processedEvents', JSON.stringify(this.events));

          this.extractAdminsList();
          this.applyLocalFilters();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Erreur lors du chargement des historiques:', error);
          this.error = 'Erreur lors du chargement des historiques';
          this.isLoading = false;
        }
      });
  }

  onPeriodChange(): void {
    const now = new Date();

    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.selectedDate = null;

    switch (this.selectedPeriod) {
      case 'today':
        const todayStart = new Date(now);
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date(now);
        todayEnd.setHours(23, 59, 59, 999);
        this.selectedStartDate = todayStart;
        this.selectedEndDate = todayEnd;
        break;

      case 'week':
        const weekStart = new Date(now);
        const dayOfWeek = weekStart.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        weekStart.setDate(weekStart.getDate() - daysFromMonday);
        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        this.selectedStartDate = weekStart;
        this.selectedEndDate = weekEnd;
        break;

      case 'month':
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        monthStart.setHours(0, 0, 0, 0);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        monthEnd.setHours(23, 59, 59, 999);
        this.selectedStartDate = monthStart;
        this.selectedEndDate = monthEnd;
        break;

      case 'calendar':
        this.openCalendarDialog();
        return;

      default:
        this.selectedStartDate = null;
        this.selectedEndDate = null;
        this.selectedDate = null;
        break;
    }

    this.applyDateFilter();
  }

  openCalendarDialog(): void {
    const dialogRef = this.dialog.open(CalendarDialogComponent, {
      width: '360px',
      panelClass: 'calendar-dialog-panel',
    });

    dialogRef.afterClosed().subscribe((result: Date | null) => {
      if (result) {
        this.selectedDate = new Date(result);
        // console.log("selected Date ===================>" + this.selectedDate);
        this.applyDateFilter();
      } else {
        this.selectedPeriod = '';
      }
    });
  }

  private applyDateFilter(): void {
    this.currentPage = 0;
    this.applyLocalFilters();
  }

  private isEventOnSelectedDate(eventTimestamp: number): boolean {
    const eventDate = new Date(eventTimestamp);

    if (this.selectedStartDate && this.selectedEndDate) {
      return eventDate >= this.selectedStartDate && eventDate <= this.selectedEndDate;
    }
    else if (this.selectedDate) {
      const selectedDay = new Date(this.selectedDate);
      const dayStart = new Date(selectedDay);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(selectedDay);
      dayEnd.setHours(23, 59, 59, 999);

      return eventDate >= dayStart && eventDate <= dayEnd;
    }

    return true;
  }

  private subscribeToServiceState(): void {
    this.authHistoryService.events$
      .pipe(takeUntil(this.destroy$))
      .subscribe(events => {
        this.events = events;
        this.extractAdminsList();
        this.applyLocalFilters();
      });

    this.authHistoryService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => this.isLoading = loading);

    this.authHistoryService.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => this.error = error);

    this.authHistoryService.pagination$
      .pipe(takeUntil(this.destroy$))
      .subscribe(pagination => this.pagination = pagination);
  }

  private extractAdminsList(): void {
    const adminsMap = new Map();
    this.events.forEach(event => {
      if (event.authDetails?.user) {
        const admin = event.authDetails.user;
        if (!adminsMap.has(admin.id)) {
          adminsMap.set(admin.id, {
            id: admin.id,
            firstName: admin.firstName,
            lastName: admin.lastName,
            email: admin.email,
            username: admin.username
          });
        }
      }
    });
    this.adminsList = Array.from(adminsMap.values())
      .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
  }

  filterByOperation(event: any): void {
    this.selectedOperationType = event.target.value;
    this.currentPage = 0;
    this.applyLocalFilters();
  }

  filterByAdmin(event: any): void {
    this.selectedAdmin = event.target.value;
    this.currentPage = 0;
    this.applyLocalFilters();
  }

  private applyLocalFilters(): void {
    this.filteredEvents = this.events.filter(event => {
      const operationMatch = !this.selectedOperationType || event.operationType === this.selectedOperationType;
      const adminMatch = !this.selectedAdmin || event.userId === this.selectedAdmin;
      const dateMatch = this.isEventOnSelectedDate(event.time);

      return operationMatch && adminMatch && dateMatch;
    });

    this.totalEvents = this.filteredEvents.length;
    this.totalPages = Math.ceil(this.totalEvents / this.pageSize);
    this.calculateTodayEvents();
    this.updatePaginatedEvents();
    this.updateDisplayPages(); // Mettre à jour les pages affichées
  }

  private calculateTodayEvents(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    this.todayEventsCount = this.events.filter(e => {
      const eventDate = new Date(e.time);
      return eventDate >= today && eventDate < tomorrow;
    }).length;
  }

  private updatePaginatedEvents(): void {
    const startIndex = this.currentPage * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedEvents = this.filteredEvents.slice(startIndex, endIndex);
  }

  // Méthode pour la pagination Material
  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedEvents();
    this.updateDisplayPages();
  }

  // Méthodes pour la pagination personnalisée
  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.updatePaginatedEvents();
      this.updateDisplayPages();
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.updatePaginatedEvents();
      this.updateDisplayPages();
    }
  }

  goToPage(page: number | string): void {
    if (typeof page === 'number' && page >= 1 && page <= this.totalPages) {
      this.currentPage = page - 1;
      this.updatePaginatedEvents();
      this.updateDisplayPages();
    }
  }

  private updateDisplayPages(): void {
    const pages: (number | string)[] = [];
    const currentPageNumber = this.currentPage + 1;

    if (this.totalPages <= 7) {
      // Afficher toutes les pages
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPageNumber <= 4) {
        // Début: 1 2 3 4 5 ... dernière
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(this.totalPages);
      } else if (currentPageNumber >= this.totalPages - 3) {
        // Fin: 1 ... avant-dernières ... dernière
        pages.push(1);
        pages.push('...');
        for (let i = this.totalPages - 4; i <= this.totalPages; i++) pages.push(i);
      } else {
        // Milieu: 1 ... avant courant courant+1 après ... dernière
        pages.push(1);
        pages.push('...');
        pages.push(currentPageNumber - 1);
        pages.push(currentPageNumber);
        pages.push(currentPageNumber + 1);
        pages.push('...');
        pages.push(this.totalPages);
      }
    }

    this.displayPages = pages;
  }

  // Obtenir les pages à afficher avec élipsis
  getDisplayPages(): any [] {
    const pages: (number | string)[] = [];

    if (this.totalPages <= 7) {
      // Afficher toutes les pages
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      const current = this.currentPage + 1;

      if (current <= 4) {
        // Début: 1 2 3 4 5 ... dernière
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(this.totalPages);
      } else if (current >= this.totalPages - 3) {
        // Fin: 1 ... avant-dernières ... dernière
        pages.push(1);
        pages.push('...');
        for (let i = this.totalPages - 4; i <= this.totalPages; i++) pages.push(i);
      } else {
        // Milieu: 1 ... avant courant courant+1 après ... dernière
        pages.push(1);
        pages.push('...');
        pages.push(current - 1);
        pages.push(current);
        pages.push(current + 1);
        pages.push('...');
        pages.push(this.totalPages);
      }
    }

    return pages;
  }

  // Méthode pour le template HTML
  isCurrentPage(page: number | string): boolean {
    return typeof page === 'number' && page === (this.currentPage + 1);
  }

  isEllipsis(page: number | string): boolean {
    return page === '...';
  }

  getResourceTypeIcon(resourceType: string): string {
    const icons: Record<string, string> = {
      'CLIENT_ROLE_MAPPING': 'link',
      'REALM_ROLE_MAPPING': 'public',
      'USER': 'person',
      'GROUP': 'group',
      'CLIENT': 'computer',
      'REALM': 'domain'
    };
    return icons[resourceType] || 'help_outline';
  }

  getOperationIcon(operationType: string): string {
    const icons: Record<string, string> = {
      'CREATE': 'add_circle',
      'UPDATE': 'edit',
      'DELETE': 'delete_outline',
      'ACTION': 'play_circle',
    };
    return icons[operationType] || 'help_outline';
  }

  getOperationLabel(operationType: string): string {
    const labels: Record<string, string> = {
      'CREATE': 'Attribution',
      'DELETE': 'Retrait',
      'UPDATE': 'Mise à jour'
    };
    return labels[operationType] || operationType;
  }

  formatTimeString(value: string): string {
    if (!value) return '';
    const [hours, minutes, seconds] = value.split(':');
    return `${+hours}h ${+minutes}min ${+seconds}s`;
  }

  // Méthodes pour le nouveau design
  formatDate(timestamp: number): string {
    if (!timestamp) return 'Date inconnue';
    const date = new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  formatTime(timestamp: number): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getRoleName(event: ProcessedEvent): string {
    if (event.representation && event.representation.length > 0 && event.representation[0].name) {
      return event.representation[0].name;
    }
    return 'Néant';
  }

  getAuthorName(event: ProcessedEvent): string {
    if (event.authDetails?.user) {
      const user = event.authDetails.user;
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
        user.username ||
        'Auteur inconnu';
    }
    return 'Auteur inconnu';
  }

  getTargetName(event: ProcessedEvent): string {
    if (event.user) {
      return `${event.user.firstName || ''} ${event.user.lastName || ''}`.trim() ||
        event.user.username ||
        'Cible inconnue';
    }
    return 'Cible inconnue';
  }




  getOperationClass(operationType: string): string {
    switch (operationType) {
      case 'CREATE':
        return 'success';
      case 'DELETE':
        return 'error';
      case 'UPDATE':
        return 'warning';
      default:
        return 'neutral';
    }
  }

  protected readonly Math = Math;
}
